<template>
    <div class="p-3">
        <div v-if="generalError" class="alert alert-danger py-2 px-3 mb-3 text-sm">
            {{ generalError }}
        </div>

        <div class="row g-3">
            <div class="col-md-6">
                <label class="form-label fw-semibold text-xs">
                    Nombre <span class="text-danger">*</span>
                </label>
                <input
                    v-model="form.name"
                    type="text"
                    class="form-control form-control-sm"
                    :class="{ 'is-invalid': errors.name }"
                    placeholder="Ej: Lonja Bogotá"
                    @blur="validateName"
                />
                <div v-if="errors.name" class="invalid-feedback d-block">
                    {{ errors.name[0] || errors.name }}
                </div>
            </div>

            <div class="col-md-6">
                <label class="form-label fw-semibold text-xs">
                    Slug
                    <span v-if="isEdit" class="text-muted fw-normal text-xxs">(no editable)</span>
                </label>
                <input
                    v-model="form.slug"
                    type="text"
                    class="form-control form-control-sm"
                    :class="{ 'is-invalid': errors.slug }"
                    :readonly="isEdit"
                    placeholder="auto-generado desde el nombre"
                />
                <div v-if="errors.slug" class="invalid-feedback d-block">
                    {{ errors.slug[0] || errors.slug }}
                </div>
                <small v-else-if="!isEdit" class="text-muted text-xxs">
                    Se genera automáticamente. Puedes editarlo antes de guardar.
                </small>
            </div>

            <div class="col-12">
                <label class="form-label fw-semibold text-xs">Logo (URL)</label>
                <input
                    v-model="form.logo"
                    type="url"
                    class="form-control form-control-sm"
                    :class="{ 'is-invalid': errors.logo }"
                    placeholder="https://..."
                />
                <div v-if="form.logo" class="mt-2">
                    <img
                        :src="form.logo"
                        alt="Preview"
                        class="logo-preview"
                        @error="onLogoError"
                    />
                </div>
                <div v-if="errors.logo" class="invalid-feedback d-block">
                    {{ errors.logo[0] || errors.logo }}
                </div>
            </div>

            <div class="col-12">
                <label class="form-label fw-semibold text-xs">Descripción</label>
                <textarea
                    v-model="form.description"
                    class="form-control form-control-sm"
                    :class="{ 'is-invalid': errors.description }"
                    rows="3"
                    placeholder="Breve descripción de la lonja..."
                ></textarea>
                <div v-if="errors.description" class="invalid-feedback d-block">
                    {{ errors.description[0] || errors.description }}
                </div>
            </div>

            <div class="col-12">
                <div class="form-check form-switch">
                    <input
                        v-model="form.active"
                        class="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="activeSwitchForm"
                    />
                    <label class="form-check-label text-sm" for="activeSwitchForm">
                        Lonja activa
                    </label>
                </div>
                <small v-if="!form.active && isEdit" class="text-warning d-block mt-1">
                    <i class="fas fa-exclamation-triangle me-1"></i>
                    Los miembros dejarán de ver el MLS de esta lonja.
                </small>
            </div>
        </div>

        <div class="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
            <soft-button variant="outline" color="secondary" size="sm" @click="$emit('close')">
                Cancelar
            </soft-button>
            <soft-button
                variant="gradient"
                color="primary"
                size="sm"
                @click="submit"
                :loading="isLoading"
            >
                <i class="fas fa-save me-1"></i>
                {{ isEdit ? 'Guardar cambios' : 'Crear lonja' }}
            </soft-button>
        </div>
    </div>
</template>

<script>
import { ref, reactive, computed, watch } from 'vue';
import SoftButton from '@/components/SoftButton.vue';
import { useLonja } from '../useLonja.js';

export default {
    name: 'FormLonja',
    components: { SoftButton },
    props: {
        lonja: { type: Object, default: null }
    },
    emits: ['close'],
    setup(props, { emit }) {
        const { createLonja, updateLonja, isLoading } = useLonja();

        const isEdit = computed(() => !!(props.lonja && props.lonja.id));

        const form = reactive({
            name: props.lonja?.name || '',
            slug: props.lonja?.slug || '',
            logo: props.lonja?.logo || '',
            description: props.lonja?.description || '',
            active: props.lonja?.active !== undefined ? !!props.lonja.active : true
        });

        const errors = ref({});
        const generalError = ref(null);

        const slugify = (text) => {
            return (text || '')
                .toString()
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .trim()
                .replace(/[^a-z0-9\s-]/g, '')
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-');
        };

        // Autogenerar slug desde el nombre en modo creación
        watch(
            () => form.name,
            (newName) => {
                if (!isEdit.value) {
                    form.slug = slugify(newName);
                }
            }
        );

        const validateName = () => {
            if (!form.name || form.name.trim().length < 3) {
                errors.value = { ...errors.value, name: 'El nombre debe tener al menos 3 caracteres' };
            } else {
                const rest = { ...errors.value };
                delete rest.name;
                errors.value = rest;
            }
        };

        const onLogoError = (e) => {
            e.target.style.display = 'none';
        };

        const submit = async () => {
            errors.value = {};
            generalError.value = null;

            if (!form.name || form.name.trim().length < 3) {
                errors.value = { name: 'El nombre debe tener al menos 3 caracteres' };
                return;
            }

            const payload = {
                name: form.name.trim(),
                slug: form.slug || slugify(form.name),
                logo: form.logo || null,
                description: form.description || null,
                active: form.active
            };

            const result = isEdit.value
                ? await updateLonja(props.lonja.id, payload)
                : await createLonja(payload);

            if (result.success) {
                emit(
                    'close',
                    isEdit.value ? 'Lonja actualizada correctamente' : 'Lonja creada correctamente'
                );
            } else if (result.errors) {
                errors.value = result.errors;
            } else {
                generalError.value = 'No se pudo guardar la lonja. Revisa los datos e intenta de nuevo.';
            }
        };

        return {
            form,
            errors,
            generalError,
            isEdit,
            isLoading,
            submit,
            validateName,
            onLogoError
        };
    }
};
</script>

<style lang="scss" scoped>
.logo-preview {
    max-height: 80px;
    max-width: 200px;
    border-radius: 0.375rem;
    border: 1px solid #e9ecef;
    padding: 4px;
    background: #fff;
}

.form-check-input:checked {
    background-color: #E53935;
    border-color: #E53935;
}

.form-control:focus,
.form-control-sm:focus {
    border-color: #ef9a9a;
    box-shadow: 0 0 0 2px rgba(229, 57, 53, 0.12);
}
</style>
